import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { trackPageView } from "@/lib/metaPixel";
import { captureCampaign } from "@/lib/gtm";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, dashboardFor } from "@/context/AuthContext";
import { LocationProvider } from "@/context/LocationContext";
import Navbar from "@/components/Navbar";
import LiveCelebration from "@/components/LiveCelebration";
import { FreeAccessBanner } from "@/components/FreeAccessBanner";
import BackButton from "@/components/BackButton";
import Footer from "@/components/Footer";
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import Vendors from "@/pages/Vendors";
import VendorDetail from "@/pages/VendorDetail";
import VendorPortal from "@/pages/VendorPortal";
import VendorProfile from "@/pages/VendorProfile";
import EventCreatorSetup from "@/pages/EventCreatorSetup";
import EventCreatorPortal from "@/pages/EventCreatorPortal";
import EventCreatorProfile from "@/pages/EventCreatorProfile";
import EventCreate from "@/pages/EventCreate";
import MarketplaceSetup from "@/pages/MarketplaceSetup";
import MarketplacePortal from "@/pages/MarketplacePortal";
import Meetups from "@/pages/Meetups";
import Subscribe from "@/pages/Subscribe";
import PaymentCallback from "@/pages/PaymentCallback";
import CompletePayment from "@/pages/CompletePayment";
import ProfileSetup from "@/pages/ProfileSetup";
import Profile from "@/pages/Profile";
import EventDetail from "@/pages/EventDetail";
import MarketplaceDetail from "@/pages/MarketplaceDetail";
import MarketplaceStore from "@/pages/MarketplaceStore";
import BundleSubscribe from "@/pages/BundleSubscribe";
import Checkout from "@/pages/Checkout";
import CheckoutCallback from "@/pages/CheckoutCallback";
import BundleOnboarding from "@/pages/BundleOnboarding";
import Events from "@/pages/Events";
import Resources from "@/pages/Resources";
import InviteAccept from "@/pages/InviteAccept";
import InviteSignUp from "@/pages/InviteSignUp";
import InviteTrialSubscribe from "@/pages/InviteTrialSubscribe";
import InviteTrialCallback from "@/pages/InviteTrialCallback";
import InvitePaymentPlan from "@/pages/InvitePaymentPlan";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import Terms from "@/pages/Terms";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import SafetyRules from "@/pages/SafetyRules";
import { Compass } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream" data-testid="loading-screen">
      <Compass className="w-10 h-10 text-sage animate-spin" style={{ animationDuration: "2.5s" }} />
    </div>
  );
}

function Protected({ roles, allowUnsubscribed = false, allowUnapproved = false, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={dashboardFor(user)} replace />;
  // 1. Profile must be completed for every non-admin role before anything else.
  if (!allowUnapproved && user.role === "parent" && !user.profile_completed) return <Navigate to="/profile-setup" replace />;
  if (!allowUnapproved && user.role === "vendor" && !user.profile_completed) return <Navigate to="/vendor-portal" replace />;
  if (!allowUnapproved && user.role === "event_creator" && !user.profile_completed) return <Navigate to="/event-creator-setup" replace />;
  if (!allowUnapproved && user.role === "marketplace_user" && !user.profile_completed) return <Navigate to="/marketplace-setup" replace />;
  // 2. Approval-required roles (vendor, event_creator, marketplace_user) that aren't approved cannot proceed.
  if (!allowUnapproved && ["vendor", "event_creator", "marketplace_user"].includes(user.role) && user.vendor_status !== "approved") {
    const back = { vendor: "/vendor-portal", event_creator: "/event-creator-setup", marketplace_user: "/marketplace-setup" }[user.role];
    return <Navigate to={back} replace />;
  }
  // 3. Everyone else must have an active subscription to reach a protected page.
  // Bundle Deal members share one subscription that covers all four role
  // profiles — treat them as active regardless of the per-role flag so
  // they're never bounced to /subscribe or /checkout from a protected page.
  const hasActiveSub =
    user.subscription_status === "active" ||
    user.subscription_status === "linked" ||
    !!user.bundle_member;
  if (!allowUnsubscribed && !["admin", "parent"].includes(user.role) && !hasActiveSub) {
    const paymentPath = {
      vendor: "/checkout?plan=vendor",
      event_creator: "/checkout?plan=eventcreator",
      marketplace_user: "/checkout?plan=marketplace",
    }[user.role] || "/subscribe";
    return <Navigate to={paymentPath} replace />;
  }
  return children;
}

// While payments are switched off, any payment/checkout page bounces to the
// user's dashboard (or sign-up for anonymous visitors).
function PaymentsGate({ children }) {
  const { user, paymentsEnabled } = useAuth();
  if (paymentsEnabled === null || (paymentsEnabled === false && user === null)) return <LoadingScreen />;
  if (paymentsEnabled === false) return <Navigate to={user ? dashboardFor(user) : "/register"} replace />;
  if (user && user.role === "parent" && new URLSearchParams(window.location.search).get("plan") !== "bundle") return <Navigate to={dashboardFor(user)} replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  return <Navigate to={dashboardFor(user)} replace />;
}

// Role-scoped browse pages. Anonymous visitors can still preview; signed-in users are
// locked to the sections that belong to their role (see spec: no cross-role access).
const PAGE_ROLES = {
  "/vendors": ["parent", "vendor", "admin"],
  "/events": ["parent", "event_creator", "admin"],
  "/meetups": ["parent", "admin"],
  "/resources": ["parent", "marketplace_user", "admin"],
};

function RoleScoped({ path, children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return children; // unauthenticated visitors can still browse
  const allowed = PAGE_ROLES[path] || [];
  if (allowed.includes(user.role)) return children;
  return <Navigate to={dashboardFor(user)} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Fire Meta Pixel PageView on every SPA route change. The initial load
  // already fires one from public/index.html so this starts tracking from
  // the second navigation onwards.
  useEffect(() => { trackPageView(); captureCampaign(); }, [location.pathname]);
  // Check URL fragment (not query params) for session_id — must be synchronous during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Navbar />
      <FreeAccessBanner />
      <LiveCelebration />
      <main className="flex-1">
        <BackButton />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/register" element={<Auth mode="register" />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/safety-rules" element={<SafetyRules />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/bundle-subscribe" element={<PaymentsGate><BundleSubscribe /></PaymentsGate>} />
          <Route path="/checkout" element={<PaymentsGate><Checkout /></PaymentsGate>} />
          <Route path="/checkout/callback" element={<Protected allowUnsubscribed allowUnapproved><CheckoutCallback /></Protected>} />
          <Route path="/bundle-onboarding" element={<BundleOnboarding />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/role-invite/:token" element={<InviteSignUp />} />
          <Route path="/invite-trial-subscribe" element={<PaymentsGate><Protected allowUnsubscribed allowUnapproved><InviteTrialSubscribe /></Protected></PaymentsGate>} />
          <Route path="/invite-payment-plan" element={<PaymentsGate><Protected allowUnsubscribed allowUnapproved><InvitePaymentPlan /></Protected></PaymentsGate>} />
          <Route path="/invite-trial-callback" element={<Protected allowUnsubscribed allowUnapproved><InviteTrialCallback /></Protected>} />
          <Route path="/vendors" element={<RoleScoped path="/vendors"><Vendors /></RoleScoped>} />
          <Route path="/vendors/:vendorId" element={<RoleScoped path="/vendors"><VendorDetail /></RoleScoped>} />
          <Route path="/events" element={<RoleScoped path="/events"><Events /></RoleScoped>} />
          <Route path="/events/:eventId" element={<RoleScoped path="/events"><EventDetail /></RoleScoped>} />
          <Route path="/marketplace/:listingId" element={<RoleScoped path="/resources"><MarketplaceDetail /></RoleScoped>} />
          <Route path="/marketplace-store/:sellerId" element={<RoleScoped path="/resources"><MarketplaceStore /></RoleScoped>} />
          <Route path="/meetups" element={<RoleScoped path="/meetups"><Meetups /></RoleScoped>} />
          <Route path="/resources" element={<RoleScoped path="/resources"><Resources /></RoleScoped>} />
          <Route path="/home" element={<HomeRedirect />} />
          <Route path="/subscribe" element={<PaymentsGate><Protected allowUnsubscribed allowUnapproved><Subscribe /></Protected></PaymentsGate>} />
          <Route path="/payment/callback" element={<Protected allowUnsubscribed allowUnapproved><PaymentCallback /></Protected>} />
          <Route path="/complete-payment" element={<CompletePayment />} />
          <Route path="/profile-setup" element={<Protected roles={["parent"]} allowUnsubscribed allowUnapproved><ProfileSetup /></Protected>} />
          <Route path="/event-creator-setup" element={<Protected roles={["event_creator"]} allowUnsubscribed allowUnapproved><EventCreatorSetup /></Protected>} />
          <Route path="/event-creator-portal" element={<Protected roles={["event_creator"]}><EventCreatorPortal /></Protected>} />
          <Route path="/event-creator/new-event" element={<Protected roles={["event_creator", "admin"]}><EventCreate /></Protected>} />
          <Route path="/marketplace-setup" element={<Protected roles={["marketplace_user"]} allowUnsubscribed allowUnapproved><MarketplaceSetup /></Protected>} />
          <Route path="/marketplace-portal" element={<Protected roles={["marketplace_user"]}><MarketplacePortal /></Protected>} />
          <Route path="/profile" element={<Protected allowUnsubscribed allowUnapproved><Profile /></Protected>} />
          <Route path="/dashboard" element={<Protected roles={["parent"]}><Dashboard /></Protected>} />
          <Route path="/vendor-portal" element={<Protected roles={["vendor"]} allowUnsubscribed allowUnapproved><VendorPortal /></Protected>} />
          <Route path="/vendor-profile" element={<Protected roles={["vendor"]} allowUnsubscribed allowUnapproved><VendorProfile /></Protected>} />
          <Route path="/event-creator-profile" element={<Protected roles={["event_creator"]} allowUnsubscribed allowUnapproved><EventCreatorProfile /></Protected>} />
          <Route path="/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
          <AppRouter />
        </LocationProvider>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
